import * as React from 'react';
import { Route, Switch, useRouteMatch, Redirect } from 'react-router-dom';

import { CoreDeviceOverviewPage } from './core-device-route';
import { CoreDevicesHub } from '../containers/core/devicesHub/coreDevicesHub';
import { CoreBatteriesHub } from '../containers/core/batteriesHub/coreBatteriesHub';
import { SelectorBarPage } from './parts/selectorBarPage';
import { Access } from './parts/access';
import { StateSwitch } from './parts/stateSwitch';
import { CorePerformance } from '../containers/core/performance';
import { useWorldRoutes } from './parts/allWorldRoutes';
import { Feature, getCoreAccessFlags, Product } from '../containers/app/productsWrapper';
import { ProductProvider } from '../context/product';
import { CoreApplications } from '../containers/core/applications/coreApplications';
import { CoreAnomalies } from '../containers/core/anomalies/coreAnomalies';
import { useCurrentUserContext } from '../context/currentUser';
import { CoreApplicationRoute } from './core-application-route';
import { CoreAnomalyRoute } from './core-anomaly-route';
import { useCurrentWorldContext } from '../context/currentWorld';
import { CoreAnomalyDevicesRoute } from './core-anomaly-devices-route';
import { CoreProcessRoute } from './core-process-route';
import { CoreProcesses } from '../containers/core/processes/coreProcesses';
import { CoreMapsRoute } from './core-maps-route';
import { CoreAccessPoints } from '../containers/core/accessPoints/coreAccessPoints';
import { useFeatureTogglesContext } from '../context/featureToggles';
import { CoreAccessPointRoute } from './core-access-point-route';
import { CoreDeviceAnomaliesBeta } from '../containers/core/deviceAnomalies/deviceAnomaliesBeta';
import { CoreDeviceAnomalyRoute } from './core-device-anomaly-route';
import { CoreDeviceAnomalyExaminationRoute } from './core-device-anomaly-examination-route';
import { CorePerformanceDevicesRoute } from './core-performance-devices-route';

interface CoreRoutesProps {
  product: Product
}

// Every Core-only route is always mounted, wrapped in <Access>, so a deep link to an unlicensed
// section renders Access Denied in place instead of falling through to the trailing Redirect.
// Capability/role/toggle conditions (Android/Windows devices, admin/manager role, feature
// toggles) are unchanged plain mount conditions - those still hide the route entirely, since
// they aren't a licence dimension.
export const CoreRoutes = (props: CoreRoutesProps) => {
  const { hasAndroidDevices, hasWindowsDevices, features } = useCurrentWorldContext();
  const { isAdministrator, isManager } = useCurrentUserContext();
  const { product } = props;
  const basePath = useRouteMatch().path;
  const routeProps = { product };
  const { showAccessPointsPage, showAnomalyDetectionBeta } = useFeatureTogglesContext();
  const worldRoutes = useWorldRoutes();
  const { hasCoreAccess, showBatteriesHub } = getCoreAccessFlags(features);
  // Finer-grained than showBatteriesHub: only used to cross-redirect /batteries/list <->
  // /batteries/devices when a world has just one of the two sub-features. Both routes are
  // always mounted regardless; showBatteriesHub alone gates access to them.
  const hasBatteries = features.includes(Feature.batteryEssentials) || features.includes(Feature.panasonicSmartBatteryMonitor);
  const hasBatteryDevices = features.includes(Feature.batteryEssentialsDevices) || features.includes(Feature.panasonicSmartBatteryMonitorDevices);
  // Only used for the bare basePath's own default landing spot - never redirects to a section the
  // world isn't licensed for, avoiding a redirect loop for the zero-access case, since
  // routes.landing renders the terminal access-denied page instead of bouncing back into /core.
  const defaultPath = hasCoreAccess ? worldRoutes.core.devices : showBatteriesHub ? worldRoutes.core.batteries : worldRoutes.landing;

  // Deliberately NO nested <Router> here. CoreRoutes used to mount its own HashRouter, giving
  // Core a second, independent history instance that only synced with the app's top-level router
  // via the browser's async hashchange event. That split every cross-router navigation into two
  // commits a task apart - the outer router (sidebar, ScrollToTop) updated in one paint and the
  // Core content swapped in a later one (or vice versa for links inside Core), visible as a flash
  // of the outgoing page. A single router keeps location updates in one commit.
  return (
    <ProductProvider product={product}>
      <Switch>
        <CoreRoute {...routeProps} path={`${basePath}/devices/list`} index={1} />
        <CoreRoute {...routeProps} path={`${basePath}/devices/map`} index={1} />
        <CoreRoute {...routeProps} path={`${basePath}/devices/:tab`} index={0} />
        <CoreRoute {...routeProps} path={`${basePath}/devices`} index={1} />
        <CoreRoute {...routeProps} path={`${basePath}/performance/:tab`} index={17} />
        <CoreRoute {...routeProps} path={`${basePath}/performance`} index={2} />
        {hasAndroidDevices && <CoreRoute {...routeProps} path={`${basePath}/applications/:tab`} index={3} />}
        {hasAndroidDevices && <CoreRoute {...routeProps} path={`${basePath}/applications`} index={4} />}
        {hasWindowsDevices && <CoreRoute {...routeProps} path={`${basePath}/processes/:tab`} index={5} />}
        {hasWindowsDevices && <CoreRoute {...routeProps} path={`${basePath}/processes`} index={6} />}
        {(isAdministrator || isManager) && <CoreRoute {...routeProps} path={`${basePath}/anomalies/details/:tab`} index={7} />}
        {(isAdministrator || isManager) && <CoreRoute {...routeProps} path={`${basePath}/anomalies/:tab`} index={8} />}
        {(isAdministrator || isManager) && <CoreRoute {...routeProps} path={`${basePath}/anomalies`} index={9} />}
        <Redirect exact path={`${basePath}/maps`} to={`${basePath}/maps/no-service`} />
        <Redirect exact path={`${basePath}/maps/location`} to={`${basePath}/devices/map`} />
        <CoreRoute {...routeProps} path={`${basePath}/maps/:tab`} index={10} />
        {showAccessPointsPage && hasAndroidDevices && <CoreRoute {...routeProps} path={`${basePath}/accessPoints/performance`} index={11} />}
        {showAccessPointsPage && hasAndroidDevices && <CoreRoute {...routeProps} path={`${basePath}/accessPoints`} index={12} />}
        {/* Must come before /anomalies-beta/details below - that route has no `exact` prop, so it
            would otherwise match this more specific path first as a prefix. */}
        {showAnomalyDetectionBeta && <CoreRoute {...routeProps} path={`${basePath}/anomalies-beta/details/examination`} index={15} />}
        {showAnomalyDetectionBeta && <CoreRoute {...routeProps} path={`${basePath}/anomalies-beta/details`} index={14} />}
        {showAnomalyDetectionBeta && <CoreRoute {...routeProps} path={`${basePath}/anomalies-beta`} index={13} />}
        {/* these must come before the CoreRoute entries below so that when only one specific
            battery sub-feature is present, the redirect wins over the (always-mounted) route
            that would otherwise match the same path first */}
        {hasBatteryDevices && !hasBatteries && <Redirect exact path={`${basePath}/batteries/list`} to={`${basePath}/batteries`} />}
        {hasBatteries && !hasBatteryDevices && <Redirect exact path={`${basePath}/batteries/devices`} to={`${basePath}/batteries`} />}
        <CoreRoute {...routeProps} path={`${basePath}/batteries/list`} index={16} accessProduct="batteryEssentials" />
        <CoreRoute {...routeProps} path={`${basePath}/batteries/devices`} index={16} accessProduct="batteryEssentials" />
        <CoreRoute {...routeProps} path={`${basePath}/batteries`} index={16} accessProduct="batteryEssentials" />
        <Redirect path={`${basePath}`} to={defaultPath} />
      </Switch>
    </ProductProvider>
  );
};

interface CoreRouteProps extends CoreRoutesProps {
  path: string,
  index: number,
  accessProduct?: Product
}

const CoreRoute = (props: CoreRouteProps) => {
  const { path, index, accessProduct = 'coreV2' } = props;
  const { isAdministrator, isManager } = useCurrentUserContext();
  const { hasAndroidDevices, hasWindowsDevices, features } = useCurrentWorldContext();
  const { showAccessPointsPage, showAnomalyDetectionBeta } = useFeatureTogglesContext();
  const { showBatteriesHub } = getCoreAccessFlags(features);

  return (
    <Route path={path}>
      <Access product={accessProduct}>
        <SelectorBarPage>
          <StateSwitch selectedIndex={index}>
            <CoreDeviceOverviewPage />
            <CoreDevicesHub />
            <CorePerformance />
            {hasAndroidDevices && <CoreApplicationRoute />}
            {hasAndroidDevices && <CoreApplications />}
            {hasWindowsDevices && <CoreProcessRoute />}
            {hasWindowsDevices && <CoreProcesses />}
            {(isAdministrator || isManager) && <CoreAnomalyDevicesRoute />}
            {(isAdministrator || isManager) && <CoreAnomalyRoute />}
            {(isAdministrator || isManager) && <CoreAnomalies />}
            <CoreMapsRoute />
            {showAccessPointsPage && hasAndroidDevices && <CoreAccessPointRoute />}
            {showAccessPointsPage && hasAndroidDevices && <CoreAccessPoints />}
            {showAnomalyDetectionBeta && <CoreDeviceAnomaliesBeta />}
            {showAnomalyDetectionBeta && <CoreDeviceAnomalyRoute />}
            {showAnomalyDetectionBeta && <CoreDeviceAnomalyExaminationRoute />}
            {showBatteriesHub && <CoreBatteriesHub />}
            <CorePerformanceDevicesRoute />
          </StateSwitch>
        </SelectorBarPage>
      </Access>
    </Route>
  );
};
